import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './Modal';
import { useAuth } from '../auth/AuthContext';
import { createLead } from '../lib/firestore';
import { logHistory } from '../lib/historyLog';
import { newLeadDefaults } from '../lib/leadDefaults';
import { fetchPipelineLeads, type PipelineLead } from '../lib/orchestratorClient';

interface Props {
  open: boolean;
  onClose: () => void;
}

function makeId(): string {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 7);
  return `lead_${t}${r}`;
}

const CREATE_LEAD_TIMEOUT_MS = 25_000;

function pipelineKind(status: string): string {
  const s = status.toLowerCase();
  if (s === 'won') return 'Client';
  if (s === 'lost') return 'Lost';
  if (s === 'nurture') return 'Nurture';
  return 'Prospect';
}

export function NewLeadDialog({ open, onClose }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pipeline, setPipeline] = useState<PipelineLead[]>([]);
  const [pipelineWarning, setPipelineWarning] = useState<string | null>(null);
  const [picked, setPicked] = useState<PipelineLead | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setName('');
    setError(null);
    setPicked(null);
    setListOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void fetchPipelineLeads().then((result) => {
      if (cancelled) return;
      setPipeline(result.items);
      setPipelineWarning(result.warning ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!listOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setListOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [listOpen]);

  const filtered = useMemo(() => {
    const q = name.trim().toLowerCase();
    if (!q) return pipeline.slice(0, 40);
    return pipeline
      .filter((row) =>
        [row.label, row.personName, row.businessName, row.email].some((v) => v.toLowerCase().includes(q)),
      )
      .slice(0, 40);
  }, [name, pipeline]);

  const choose = (row: PipelineLead) => {
    setPicked(row);
    setName(row.label);
    setListOpen(false);
    setError(null);
  };

  const onNameChange = (value: string) => {
    setName(value);
    if (picked && value.trim() !== picked.label) setPicked(null);
    setListOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!name.trim()) {
      setError('Business name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);

    const id = makeId();
    const record = newLeadDefaults({
      id,
      ownerUid: user.uid,
      ownerEmail: user.email ?? undefined,
      businessName: name.trim(),
      phone: picked?.phone,
      email: picked?.email,
      logicBrooksLeadId: picked?.id,
    });

    try {
      await Promise.race([
        createLead(record),
        new Promise<never>((_, rej) =>
          setTimeout(
            () =>
              rej(
                new Error(
                  `Save still pending after ${CREATE_LEAD_TIMEOUT_MS / 1000}s — check network, then retry.`,
                ),
              ),
            CREATE_LEAD_TIMEOUT_MS,
          ),
        ),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    } finally {
      setSubmitting(false);
    }

    logHistory({
      leadId: id,
      user,
      kind: 'status_change',
      payload: { from: null, to: 'Discovery', via: 'new_preview_dialog' },
    });
    reset();
    onClose();
    navigate(`/leads/${id}`);
  };

  return (
    <Modal open={open} onClose={onClose} title="New preview">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-nm-muted">Business name</span>
          <div ref={boxRef} className="relative mt-1">
            <input
              autoFocus
              role="combobox"
              aria-expanded={listOpen}
              aria-controls="pipeline-lead-list"
              aria-autocomplete="list"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              onFocus={() => setListOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setListOpen(false);
              }}
              placeholder="Pick a pipeline business, or type a new name"
              className="fp-input"
            />
            {listOpen && filtered.length > 0 && (
              <ul
                id="pipeline-lead-list"
                role="listbox"
                className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-[12px] border border-nm-border bg-nm-card py-1 shadow-lg"
              >
                {filtered.map((row) => (
                  <li key={row.id} role="option" aria-selected={picked?.id === row.id}>
                    <button
                      type="button"
                      onClick={() => choose(row)}
                      className="flex w-full items-baseline justify-between gap-2 px-3 py-2 text-left hover:bg-nm-surface"
                    >
                      <span className="min-w-0 truncate text-sm text-nm-ink">{row.label}</span>
                      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wide text-nm-tertiary">
                        {pipelineKind(row.status)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {picked ? (
            <p className="mt-1.5 text-xs text-nm-muted">
              From pipeline · {pipelineKind(picked.status)}
              {picked.email ? ` · ${picked.email}` : ''}
              {picked.phone ? ` · ${picked.phone}` : ''}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-nm-tertiary">Or type a name that is not in the list for a fresh sheet.</p>
          )}
          <p className="mt-1.5 text-xs text-nm-tertiary">Industry is the first beat on the call sheet.</p>
          {pipelineWarning && <p className="mt-1.5 text-xs text-nm-muted">{pipelineWarning}</p>}
        </div>

        {error && (
          <div className="rounded-md border border-nm-danger/30 bg-nm-danger-bg px-3 py-2 text-xs text-nm-danger">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="fp-btn fp-btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="fp-btn fp-btn-primary disabled:opacity-50"
          >
            {submitting ? 'Creating...' : 'Start call sheet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
